import { Box, Stack, Text } from '@chakra-ui/react';
import { setupI18n } from '@lingui/core';
import { I18nProvider } from '@lingui/react';
import { useLingui } from '@lingui/react/macro';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useMemo, useState } from 'react';

import { AuthLegalNotice } from '#modules/user/features/auth/components/AuthLegalNotice';
import { AuthPageLayout } from '#modules/user/features/auth/components/AuthPageLayout';
import { ReCaptchaCaption } from '#features/form';
import { LanguageSwitcherView } from '#features/i18n/components/LanguageSwitcher';
import { messages as enMessages } from '#features/i18n/locales/en/messages.po';
import { messages as ruMessages } from '#features/i18n/locales/ru/messages.po';
import { SlimFooter } from '#features/layout/components/SlimFooter';
import { Button, Link, PageHeading } from '#features/ui';

export const Route = createFileRoute('/{-$locale}/_layout/preview/language-switcher')({
  ssr: false,
  staticData: { rendersOwnFooter: true },
  beforeLoad: () => {
    if (!import.meta.env.DEV) throw notFound();
  },
  validateSearch: (search: Record<string, unknown>) => ({
    lang: search.lang === 'en' ? ('en' as const) : ('ru' as const),
    surface:
      search.surface === 'sign-in'
        ? 'sign-in'
        : search.surface === 'sign-up'
          ? 'sign-up'
          : 'controls',
    state:
      search.state === 'pending' ? 'pending' : search.state === 'disabled' ? 'disabled' : 'normal',
  }),
  head: () => ({ meta: [{ name: 'robots', content: 'noindex, nofollow' }] }),
  component: LanguagePreview,
});

/** Controlled interactions never write cookies/profile preferences or navigate. */
function LanguagePreview() {
  const { lang, state, surface } = Route.useSearch();
  const i18n = useMemo(
    () => setupI18n({ locale: lang, messages: { en: enMessages, ru: ruMessages } }),
    [lang]
  );
  const [selected, setSelected] = useState(lang);
  const props = {
    currentLocale: selected,
    pendingLocale:
      state === 'pending' ? (selected === 'en' ? ('ru' as const) : ('en' as const)) : null,
    disabled: state === 'disabled',
    onChange: setSelected,
  };
  return (
    <I18nProvider i18n={i18n}>
      {surface !== 'controls' ? (
        <AuthPreview signup={surface === 'sign-up'} disabled={state !== 'normal'} />
      ) : (
        <Stack w="100%" maxW="1100px" mx="auto" px={{ base: 4, md: 6 }} py={6} gap={6}>
          <Box data-preview="short">
            <LanguageSwitcherView {...props} />
          </Box>
          <Box data-preview="native">
            <LanguageSwitcherView {...props} compact={false} />
          </Box>
          <Box data-preview="auth">
            <LanguageSwitcherView {...props} presentation="picker" />
          </Box>
          <SlimFooter />
        </Stack>
      )}
    </I18nProvider>
  );
}

/** Real auth layout and language control with inert representative form actions. */
function AuthPreview({ signup, disabled }: { signup: boolean; disabled: boolean }) {
  const { t } = useLingui();
  const providers = ['Google', 'Discord'];
  return (
    <AuthPageLayout languageDisabled={disabled}>
      <PageHeading
        align="center"
        description={
          signup ? (
            <>
              {t`signUp.alreadyRegistered`} <Link to="/{-$locale}/sign-in">{t`signUp.signIn`}</Link>
            </>
          ) : (
            <>
              {t`signIn.noAccount`} <Link to="/{-$locale}/sign-up">{t`signIn.createAccount`}</Link>
            </>
          )
        }
      >
        {signup ? t`signUp.heading` : t`signIn.welcome`}
      </PageHeading>
      <Stack gap={6}>
        <Stack gap={4}>
          {!signup && (
            <Text
              textStyle="caption"
              color="fg.muted"
              textAlign="center"
            >{t`Use the method you used before.`}</Text>
          )}
          <Stack gap={3}>
            {providers.map((providerName) => (
              <Button
                key={providerName}
                size="lg"
                variant="outline"
                type="button"
                w="full"
                h="auto"
                minH={12}
                py={3}
                whiteSpace="normal"
              >
                {signup ? t`Sign up with ${providerName}` : t`Sign in with ${providerName}`}
              </Button>
            ))}
          </Stack>
          <Button size="lg" variant="ghost" type="button">
            {signup ? t`Sign up with email` : t`Sign in with email`}
          </Button>
          <Button size="lg" variant="ghost" type="button">
            {signup ? t`Other ways to sign up` : t`Other ways to sign in`}
          </Button>
        </Stack>
        <ReCaptchaCaption />
        <AuthLegalNotice />
      </Stack>
    </AuthPageLayout>
  );
}
